import { ArrowBack, InfoOutlined, Save, Security } from "@mui/icons-material";
import { Alert, Box, Button, Checkbox, CircularProgress, FormControlLabel, Grid, Paper, Radio, RadioGroup, Typography } from "@mui/material";
import dayjs from "dayjs";
import { useAtom } from "jotai";
import { FC, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { permissionsAtom } from "../../atoms/atom";
import AppBar from "../../components/app-bar/AppBar";
import { useAlert } from "../../components/contextProviders/AlertProvider";
import PrivacyPolicyLink from "../../components/privacy-policy-link/PrivacyPolicyLink";
import RequiredDocuments from "../../components/required-documents/RequiredDocuments";
import { LgpdService } from "../../services/LgpdService";
import { LgpdPolicyDTO } from "../../types/LgpdPolicyDTO";
import { useUtils } from "../../utils/utilFunctions";
import "./AuditLGPD.scss";

const AuditLGPD: FC = () => {
	const [policy, setPolicy] = useState<LgpdPolicyDTO>({} as LgpdPolicyDTO);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [permissions] = useAtom(permissionsAtom);
	const navigate = useNavigate();
	const { verifyPermission } = useUtils();
	const requiredDocuments = policy.requiredDocuments ? policy.requiredDocuments.split(",").filter(Boolean) : [];

	const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const { name, checked } = event.target;
		setPolicy((prev) => ({ ...prev, [name]: checked }));
	};

	const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setPolicy((prev) => ({ ...prev, deleteAfterDays: event.target.value }));
	};

	const handleRequiredDocumentsChange = (requiredDocuments: string[]) => {
		setPolicy((prev) => ({ ...prev, requiredDocuments: requiredDocuments.join(",") }));
	};

	const { id } = useParams<{ id: string }>();
	const service = new LgpdService();
	const { showAlert, showError } = useAlert();

	const handleSave = async () => {
		if (!id) return;
		setSaving(true);
		try {
			const response = await service.updatePolicy(id, policy);
			setPolicy(response.data);
			showAlert("Política de LGPD salva com sucesso!");
		} catch (error) {
			showError("Erro ao salvar a política de LGPD.");
		} finally {
			setSaving(false);
		}
	};

	useEffect(() => {
		if (!permissions.length) {
			verifyPermission(permissions, 'LGPD_POLICY_CONFIG');
		}
	}, []);

	useEffect(() => {
		if (!id) return;
		const loadPolicy = async () => {
			setLoading(true);
			try {
				const response = await service.getPolicy(id);
				setPolicy(response.data);
			} catch (error) {
				showError("Erro ao carregar configurações de LGPD.");
			} finally {
				setLoading(false);
			}
		};
		loadPolicy();
	}, [id]);

	return (
		<>
			<AppBar
				modulo="LGPD"
			/>
			<div className="audit-lgpd">
				<div className="top">
					<Button
						variant="contained"
						color="info"
						onClick={() => navigate('/auditoria')}
						startIcon={<ArrowBack />}
					>
						Voltar para empresas
					</Button>
				</div>
				{loading ? <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box> :
					<Box className="lgpd-policy-container">
					<Alert icon={<Security fontSize="inherit" />} severity="info" variant="outlined">
						Esta política define o tempo de retenção e as regras para exclusão automática de documentos, conforme os princípios da LGPD.
						<br /> Todas as exclusões serão registradas nos LOGS do sistema para auditoria e conformidade.
						<br /> As regras aqui definidas devem estar alinhadas à <PrivacyPolicyLink label="Política de Privacidade e LGPD" /> divulgada aos titulares.
					</Alert>
					<Grid container spacing={3}>
						<Grid item xs={12} md={4}>
							<Paper className="policy-card">
								<Typography variant="h6" gutterBottom>
								Exclusão automática
								</Typography>
								<FormControlLabel
									control={
										<Checkbox
											name="autoDeleteEnabled"
											checked={policy.autoDeleteEnabled || false}
											onChange={handleCheckboxChange}
										/>
									}
									label="Ativar exclusão automática de documentos"
								/>
								<Typography variant="body2" color="text.secondary" className="helper-text">
									O sistema excluirá automaticamente os documentos conforme as regras configuradas abaixo.
								</Typography>
								<Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
									Excluir documentos após
								</Typography>
								<RadioGroup
									name="deleteAfterDays"
									value={policy.deleteAfterDays || null}
									onChange={handleRadioChange}
								>
									<FormControlLabel disabled={!policy.autoDeleteEnabled} value="30" control={<Radio />} label="30 dias" />
									<FormControlLabel disabled={!policy.autoDeleteEnabled} value="60" control={<Radio />} label="60 dias" />
									<FormControlLabel disabled={!policy.autoDeleteEnabled} value="90" control={<Radio />} label="90 dias" />
									<FormControlLabel disabled={!policy.autoDeleteEnabled} value="180" control={<Radio />} label="180 dias" />
									<FormControlLabel disabled={!policy.autoDeleteEnabled} value="365" control={<Radio />} label="365 dias" />
								</RadioGroup>
								{policy.autoDeleteEnabled && <Alert icon={<InfoOutlined />} severity="info" className="info-box">
									Documentos serão excluídos automaticamente em:
									<br />
									<strong>
										{policy.deleteAfterDays ? `${policy.deleteAfterDays} dias` : 'Nunca'}
									</strong>
									<br />
									Conforme política da empresa.
								</Alert>}
							</Paper>
						</Grid>
						<Grid item xs={12} md={4} className="column-flex">
							<Paper className="policy-card">
								<Typography variant="h6" gutterBottom>
									Aplicar para
								</Typography>
								<FormControlLabel
									control={<Checkbox name="applyToRejectedCandidates" checked={policy.applyToRejectedCandidates || false} onChange={handleCheckboxChange} />}
									label="Documentos de candidatos reprovados"
								/>
								<FormControlLabel
									control={<Checkbox name="applyToCanceledAdmissions" checked={policy.applyToCanceledAdmissions || false} onChange={handleCheckboxChange} />}
									label="Documentos de admissões canceladas"
								/>
								<FormControlLabel
									control={<Checkbox name="applyToUnfinishedRegistrations" checked={policy.applyToUnfinishedRegistrations || false} onChange={handleCheckboxChange} />}
									label="Documentos de candidatos que nunca finalizaram o cadastro"
								/>
								<FormControlLabel
									control={<Checkbox name="applyToApprovedCandidates" checked={policy.applyToApprovedCandidates || false} onChange={handleCheckboxChange} />}
									label="Documentos de candidatos aprovados"
								/>
								<Typography variant="body2" color="text.secondary" className="helper-text">
									Documentos de candidatos aprovados normalmente são migrados para o prontuário do empregado.
								</Typography>
							</Paper>
							<Paper className="policy-card">
								<Typography variant="h6" gutterBottom>
									Antes da exclusão
								</Typography>
								<FormControlLabel
									control={<Checkbox name="notifyHrBeforeDelete" checked={policy.notifyHrBeforeDelete || false} onChange={handleCheckboxChange} />}
									label="Enviar aviso por e-mail ao RH 15 dias antes"
								/>
								<FormControlLabel
									control={<Checkbox name="registerLogs" checked={policy.registerLogs || false} onChange={handleCheckboxChange} />}
									label="Registrar a exclusão nos LOGS"
								/>
								<Typography variant="body2" color="text.secondary" className="helper-text">
									Estas ações garantem transparência e rastreabilidade conforme exigido pela <a href="https://www.planalto.gov.br/ccivil_03/_ato2015-2018/2018/lei/l13709.htm" target="_blank" rel="noreferrer">LGPD</a>.
								</Typography>
							</Paper>
						</Grid>
						<Grid item xs={12} md={4}>
						<Paper className="policy-card">
							<Typography variant="h6" gutterBottom>
								Resumo da política
							</Typography>
							<Box className="summary-row">
								<Typography variant="body2" className="summary-label">Exclusão automática</Typography>
								<Typography variant="body2" color={policy.autoDeleteEnabled ? "success.main" : "text.secondary"}>
									{policy.autoDeleteEnabled ? 'Ativada' : 'Desativada'}
								</Typography>
							</Box>
							<Box className="summary-row">
								<Typography variant="body2" className="summary-label">Prazo de exclusão</Typography>
								<Typography variant="body2" color="success.main">
									{policy.deleteAfterDays ? `${policy.deleteAfterDays} dias` : 'Nunca'}
								</Typography>
							</Box>
							<Box className="summary-row">
								<Typography variant="body2" className="summary-label">Documentos obrigatórios</Typography>
								<Typography variant="body2" color={requiredDocuments.length ? "success.main" : "text.secondary"}>
									{requiredDocuments.length ? `${requiredDocuments.length} documento(s)` : 'Nenhum'}
								</Typography>
							</Box>
							<Box className="summary-row">
								<Typography variant="body2" className="summary-label">Última atualização</Typography>
								<Typography variant="body2" color="text.secondary">{policy.lastUpdated ? dayjs(policy.lastUpdated).format('DD/MM/YYYY HH:mm') : '-'}</Typography>
							</Box>
							<Box className="summary-row">
								<Typography variant="body2" className="summary-label">Atualizado por</Typography>
								<Typography variant="body2" color="text.secondary">{policy.updatedBy || '-'}</Typography>
							</Box>
						</Paper>
						</Grid>
						<Grid item xs={12}>
							<RequiredDocuments
								requiredDocuments={requiredDocuments}
								onChange={handleRequiredDocumentsChange}
							/>
						</Grid>
					</Grid>
					<Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
						<Button
							variant="contained"
							color="success"
							startIcon={<Save />}
							disabled={saving}
							size="large"
							onClick={handleSave}
						>
							{saving ? <CircularProgress size={24} color="inherit" /> : 'SALVAR CONFIGURAÇÕES'}
						</Button>
					</Box>
				</Box>
				}
			</div>
		</>
	)
}

export default AuditLGPD;